import { Select, TextInput } from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col } from 'reactstrap';
import { useMemo, useEffect } from 'react';

import { useParameters } from '../../hooks/useParameters';
import { useValidations } from '../../hooks/useValidations';
import { IdentificationFormProps, IdentificationInputs } from './types';

const IdentificationFormFieldsV2 = ({
  identificationTypes = [],
}: IdentificationFormProps) => {
  const { t } = useTranslation();
  const { countryOptions } = useParameters();

  const {
    msgValidations,
    validateRut,
    validateTextNotEmpty,
  } = useValidations();

  const { control, watch, setValue } = useFormContext<IdentificationInputs>();

  const selectedIdentificationOption = watch('identificationType');

  const identificationTypesOptions = useMemo(
    () => identificationTypes?.map((e) => ({ label: e.name, value: e.id })),
    [identificationTypes],
  );

  const selectedIdentification = useMemo(
    () =>
      identificationTypes?.find(
        (e) => e.id === selectedIdentificationOption?.value,
      ),
    [identificationTypes, selectedIdentificationOption?.value],
  );

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined = undefined;
    if (identificationTypes?.length && !selectedIdentification) {
      const defaultIdentification = identificationTypes[0];
      timerId = setTimeout(() => {
        setValue('identificationType', {
          value: defaultIdentification.id,
          label: defaultIdentification.name,
        });
      }, 500);
    }

    return () => {
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
    };
  }, [identificationTypes, setValue, selectedIdentification]);

  if (!selectedIdentification) {
    return <></>;
  }

  return (
    <>
      <Col xs={12}>
        <Select
          options={identificationTypesOptions}
          name="identificationType"
          control={control}
          label={t(`loginTuitionProcess.identificationType`)}
          isClearable={false}
          rules={{
            required: msgValidations.required,
          }}
        />
      </Col>
      {!!(selectedIdentification?.id && selectedIdentification?.countryId) && (
        <Col xs={12}>
          <TextInput
            name="identificationValue"
            label={selectedIdentification.name}
            control={control}
            formatter={
              selectedIdentification?.id === 'CL_RUT' ? 'rut' : undefined
            }
            rules={{
              validate:
                selectedIdentification?.id === 'CL_RUT'
                  ? {
                      empty: validateTextNotEmpty,
                      rut: validateRut,
                    }
                  : { empty: validateTextNotEmpty },
            }}
            shouldUnregister={true}
          />
        </Col>
      )}

      {!!(selectedIdentification?.id && !selectedIdentification?.countryId) && (
        <>
          <Col xs={12} md={6} className="pr-md-1">
            <Select
              name="identificationCountry"
              label={t(`loginTuitionProcess.identificationCountry`)}
              control={control}
              options={countryOptions}
              rules={{
                required: msgValidations.required,
              }}
              shouldUnregister={true}
              isClearable={false}
            />
          </Col>
          <Col xs={12} md={6} className="pl-md-1">
            <TextInput
              name="identificationValue"
              label={selectedIdentification.name}
              rules={{
                validate: {
                  empty: validateTextNotEmpty,
                },
              }}
              control={control}
              shouldUnregister={true}
            />
          </Col>
        </>
      )}
    </>
  );
};

export default IdentificationFormFieldsV2;
